import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditAccountType,
  getIsAccountTypeModalOpen,
  toggleAccountTypeModal,
} from "../../features/accounts/accountTypesSlice";
import { IFormInput } from "../../types/accountTypes";
import { t } from "../../locales";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

type Props = {
  handleSaveAccountType: (data: IFormInput) => void;
};

const AccountTypeFormModal = ({ handleSaveAccountType }: Props) => {
  const dispatch = useAppDispatch();
  const editAccountType = useAppSelector(getEditAccountType);
  const isAccountTypeModalOpen = useAppSelector(getIsAccountTypeModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { name: "", phone: "", description: "" },
  });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveAccountType(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleAccountTypeModal(false));
  };

  useEffect(() => {
    if (editAccountType && editAccountType.id) {
      reset({
        name: editAccountType.name,
        phone: editAccountType.phone,
        description: editAccountType.description,
      });
    } else {
      reset({ name: "", phone: "", description: "" });
    }
  }, [editAccountType, isAccountTypeModalOpen]);

  return (
    <Dialog
      open={isAccountTypeModalOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseFormModal();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {t.accountTypes.formTitle}
          </DialogTitle>
          <DialogDescription>
            {editAccountType
              ? `Editing: ${editAccountType.name}`
              : t.accountTypes.addNewAccountType}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name">{t.accountTypes.nameLabel}</Label>
            <Input
              id="name"
              placeholder={t.accountTypes.namePlaceholder}
              {...register("name", { required: true })}
              className="h-11"
            />
            {errors.name && errors.name.type === "required" && (
              <span className="text-red-500 text-sm">
                {t.accountTypes.nameRequired}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">{t.accountTypes.phoneLabel}</Label>
            <Input
              id="phone"
              placeholder={t.accountTypes.phonePlaceholder}
              {...register("phone", { required: false })}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t.accountTypes.descriptionLabel}</Label>
            <Textarea
              id="description"
              rows={3}
              placeholder={t.accountTypes.descriptionPlaceholder}
              {...register("description", { required: false })}
            />
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.accountTypes.cancelButton}
            </Button>
            <Button
              type="submit"
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              disabled={isLoading}
            >
              {isLoading ? t.accountTypes.saving : t.accountTypes.saveButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AccountTypeFormModal;
